import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DownloadIcon } from "@heroicons/react/outline";

import AccountingWrapper from "../../../../../Components/v2/Accounting/AccountingWrapper";
import AccountingPageHeader from "../../../../../Components/v2/Accounting/AccountingPageHeader";
import FormDateRange from "../../../../../Components/v2/Form/FormDateRange";
import FormSelect from "../../../../../Components/v2/Form/FormSelect";
import FormError from "../../../../../Components/v2/Form/FormError";
import {
  getPartnerLedger,
  listAllPartners,
  useAuthState,
} from "../../../../../Context";
import { EXPORTS } from "../../../../../Constants/accountingReport";
import {
  formatPeriodDate,
  toWibPickerDate,
  wibEndOfDay,
  wibStartOfDay,
} from "../../../../../Utils/PeriodUtils";
import { Money, formatRupiah } from "../VatRecap/sections";
import {
  EmptyState,
  LoadError,
  ReportTypeTabs,
  TD,
  TD_NUM,
  TH,
  TH_NUM,
  useReportExport,
} from "./shared";
import {
  RECEIVABLE,
  defaultLedgerRange,
  entryText,
  typeFromParam,
} from "./utils";

const SummaryItem = ({ label, value, footer }) => (
  <div className="min-w-0 bg-white px-4 py-4">
    <dt className="text-sm text-gray-600">{label}</dt>
    <dd className="mt-1 text-lg font-semibold tabular-nums text-gray-900 [overflow-wrap:anywhere]">
      {value}
    </dd>
    {footer && <dd className="mt-1 text-sm text-gray-600">{footer}</dd>}
  </div>
);

const LedgerSummary = ({ ledger, type }) => {
  const { t } = useTranslation();
  return (
    <dl
      aria-label={t("reports.partnerLedger.summary")}
      className="grid grid-cols-1 gap-px overflow-hidden rounded-lg border border-gray-200 bg-gray-200 shadow-sm sm:grid-cols-2 xl:grid-cols-4"
    >
      <SummaryItem
        label={t("reports.partnerLedger.opening")}
        value={formatRupiah(ledger.openingBalance)}
      />
      <SummaryItem
        label={t("reports.partnerLedger.increase")}
        value={formatRupiah(ledger.totalIncrease)}
      />
      <SummaryItem
        label={t("reports.partnerLedger.decrease")}
        value={formatRupiah(ledger.totalDecrease)}
      />
      <SummaryItem
        label={t("reports.partnerLedger.closing")}
        value={formatRupiah(ledger.closingBalance)}
        footer={
          type === RECEIVABLE && Number(ledger.deposit) > 0
            ? t("reports.partnerLedger.depositLeft", {
                amount: formatRupiah(ledger.deposit),
              })
            : null
        }
      />
    </dl>
  );
};

const LedgerTable = ({ ledger }) => {
  const { t, i18n } = useTranslation();
  return (
    <section aria-labelledby="partner-ledger-history">
      <h3
        id="partner-ledger-history"
        className="mb-2 text-base font-semibold text-gray-900"
      >
        {t("reports.partnerLedger.history")}
      </h3>
      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={TH}>
                {t("reports.partnerLedger.columns.date")}
              </th>
              <th scope="col" className={TH}>
                {t("reports.partnerLedger.columns.transaction")}
              </th>
              <th scope="col" className={TH}>
                {t("reports.partnerLedger.columns.description")}
              </th>
              <th scope="col" className={TH_NUM}>
                {t("reports.partnerLedger.columns.increase")}
              </th>
              <th scope="col" className={TH_NUM}>
                {t("reports.partnerLedger.columns.decrease")}
              </th>
              <th scope="col" className={TH_NUM}>
                {t("reports.partnerLedger.columns.balance")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white text-gray-900">
            <tr className="bg-gray-50/50">
              <td className={`${TD} text-gray-600`}>
                {formatPeriodDate(ledger.startDate, i18n.language)}
              </td>
              <td colSpan={4} className={`${TD} text-gray-600`}>
                {t("reports.partnerLedger.opening")}
              </td>
              <td className={`${TD_NUM} font-medium`}>
                <Money value={ledger.openingBalance} />
              </td>
            </tr>
            {ledger.entries.map((entry) => {
              return (
                <tr key={entry.id}>
                  <td className={TD}>
                    {formatPeriodDate(entry.date, i18n.language)}
                  </td>
                  <td className={TD}>
                    {entryText(
                      t(`reports.partnerLedger.entryTypes.${entry.type}`, {
                        defaultValue: entry.label,
                      }),
                      entry,
                      (number) =>
                        t("reports.partnerLedger.settles", { number }),
                    )}
                  </td>
                  <td className="max-w-xs px-3 py-2 text-sm text-gray-600">
                    {entry.description || "-"}
                  </td>
                  <td
                    className={`${TD_NUM} ${entry.increase ? "" : "text-gray-500"}`}
                  >
                    <Money value={entry.increase} />
                  </td>
                  <td
                    className={`${TD_NUM} ${entry.decrease ? "" : "text-gray-500"}`}
                  >
                    <Money value={entry.decrease} />
                  </td>
                  <td className={`${TD_NUM} font-medium`}>
                    <Money value={entry.balance} />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 font-semibold text-gray-900">
            <tr>
              <td colSpan={3} className={TD}>
                {t("reports.partnerLedger.periodTotal")}
              </td>
              <td className={TD_NUM}>
                <Money value={ledger.totalIncrease} />
              </td>
              <td className={TD_NUM}>
                <Money value={ledger.totalDecrease} />
              </td>
              <td className={TD_NUM}>
                <Money value={ledger.closingBalance} />
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      {ledger.entries.length === 0 && (
        <p className="mt-2 text-sm text-gray-600">
          {t("reports.partnerLedger.noMovement")}
        </p>
      )}
    </section>
  );
};

const AccountingPartnerLedger = () => {
  const { t } = useTranslation();
  const { user } = useAuthState();
  const business = user?.mainBusiness;
  const businessID = business?.id;

  // Contact, type and period live in the URL so the aging page can link here
  // and a refresh keeps the view.
  const [searchParams, setSearchParams] = useSearchParams();
  const type = typeFromParam(searchParams.get("type"));
  const partnerID = searchParams.get("partnerId") || "";
  const startParam = searchParams.get("startDate");
  const endParam = searchParams.get("endDate");
  const [defaultStart, defaultEnd] = useMemo(
    () => defaultLedgerRange(business?.fiscalYearStartMonth),
    [business?.fiscalYearStartMonth],
  );
  const startDate = useMemo(
    () => (startParam ? toWibPickerDate(startParam) : defaultStart),
    [startParam, defaultStart],
  );
  const endDate = useMemo(
    () => (endParam ? toWibPickerDate(endParam) : defaultEnd),
    [endParam, defaultEnd],
  );
  // The picker holds the range being picked until both ends are chosen.
  const [range, setRange] = useState([startDate, endDate]);
  useEffect(() => setRange([startDate, endDate]), [startDate, endDate]);

  const apiStart = wibStartOfDay(startDate);
  const apiEnd = wibEndOfDay(endDate);

  // Every contact, not the shared list's first page. null until loaded.
  const [partners, setPartners] = useState(null);
  const [partnersError, setPartnersError] = useState(null);
  const [partnersAttempt, setPartnersAttempt] = useState(0);
  useEffect(() => {
    if (!businessID) return undefined;
    let cancelled = false;
    setPartners(null);
    setPartnersError(null);
    listAllPartners(businessID)
      .then((items) => {
        if (!cancelled) setPartners(items);
      })
      .catch((err) => {
        if (!cancelled) setPartnersError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [businessID, partnersAttempt]);

  const partnerOptions = useMemo(
    () =>
      (partners || []).map((item) => ({
        ...item,
        value: item.id,
        label: item.name,
      })),
    [partners],
  );

  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [ledgerAttempt, setLedgerAttempt] = useState(0);

  useEffect(() => {
    if (!businessID || !partnerID) {
      setLedger(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPartnerLedger({
      businessID,
      partnerID,
      type,
      startDate: apiStart,
      endDate: apiEnd,
    })
      .then((data) => {
        if (!cancelled) setLedger(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setLedger(null);
          setError(err);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [businessID, partnerID, type, apiStart, apiEnd, ledgerAttempt]);

  // A contact missing from the full list is either deleted, which still has
  // its history, or an id that was never this business's: nothing to show.
  const partnerListed = partnerOptions.some(
    (option) => option.value === partnerID,
  );
  const unknownPartner =
    !!partnerID &&
    partners !== null &&
    !partnerListed &&
    !!ledger &&
    !loading &&
    ledger.entries.length === 0 &&
    !ledger.openingBalance;
  // The picker names the contact from the ledger when the list cannot.
  const partner =
    partnerOptions.find((option) => option.value === partnerID) ||
    (partnerID && ledger && !unknownPartner
      ? { value: partnerID, label: ledger.partnerName }
      : null);

  const { exportAs, exporting, errorModal } = useReportExport((exportType) =>
    getPartnerLedger({
      businessID,
      partnerID,
      type,
      startDate: apiStart,
      endDate: apiEnd,
      exportType,
    }),
  );

  const setParams = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  const onRangeChange = ([start, end]) => {
    setRange([start, end]);
    if (start && end) {
      setParams({ startDate: wibStartOfDay(start), endDate: wibEndOfDay(end) });
    }
  };

  const renderBody = () => {
    if (!partnerID) {
      return (
        <EmptyState
          title={t("reports.partnerLedger.empty.title")}
          description={t("reports.partnerLedger.empty.description")}
        />
      );
    }
    if (error && !loading) {
      return (
        <LoadError
          error={error}
          onRetry={() => setLedgerAttempt((n) => n + 1)}
        />
      );
    }
    if (loading || !ledger) {
      return (
        <div
          className="h-40 animate-pulse rounded-lg border border-gray-200 bg-gray-50"
          aria-busy="true"
        />
      );
    }
    if (unknownPartner) {
      return (
        <EmptyState
          title={t("reports.partnerLedger.unknown.title")}
          description={t("reports.partnerLedger.unknown.description")}
        />
      );
    }
    return (
      <>
        <LedgerSummary ledger={ledger} type={type} />
        <LedgerTable ledger={ledger} />
        <p className="text-sm text-gray-600">
          <Link
            to={`/v2/accounting/reports/aging${type === RECEIVABLE ? "" : `?type=${type}`}`}
            className="font-medium text-blue-600 hover:underline"
          >
            {t(`reports.partnerLedger.toAging.${type}`)}
          </Link>
        </p>
      </>
    );
  };

  return (
    <AccountingWrapper>
      <main className="lg:col-span-9">
        <AccountingPageHeader
          title="reports.partnerLedger.name"
          // Nothing to export until a contact is chosen. The header shows a
          // spinner whenever the action is disabled, so it is left out instead.
          dropDownAction={
            partnerID && !unknownPartner
              ? {
                  label: t("Export"),
                  icon: <DownloadIcon className="h-5 w-5" />,
                  onClick: () => exportAs("pdf"),
                  items: EXPORTS.map((item) => ({
                    name: t(item.label),
                    onClick: () => exportAs(item.value),
                  })),
                  disabled: exporting,
                }
              : undefined
          }
        />
        <ReportTypeTabs
          value={type}
          onChange={(next) =>
            setParams({ type: next === RECEIVABLE ? null : next })
          }
        />
        <div className="space-y-6 px-4 py-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <FormSelect
                required
                id="partner-ledger-partner"
                label="reports.partnerLedger.partner"
                searchable
                options={partnerOptions}
                isLoading={!partners && !partnersError}
                value={partner}
                onChange={(value) => setParams({ partnerId: value?.value })}
                placeholder={t("reports.partnerLedger.partnerPlaceholder")}
              />
              {/* The picker's own options failed: a banner beside it, not the
                  block that stands in for a whole report. */}
              <FormError
                className="mt-2"
                error={partnersError}
                onRetry={() => setPartnersAttempt((n) => n + 1)}
              />
            </div>
            <FormDateRange
              id="partner-ledger-period"
              label="reports.partnerLedger.period"
              startDate={range[0]}
              endDate={range[1]}
              onChange={onRangeChange}
            />
          </div>
          {renderBody()}
        </div>
        {errorModal}
      </main>
    </AccountingWrapper>
  );
};

export default AccountingPartnerLedger;
